import React from 'react'
import { GetServerSideProps, NextPage } from 'next'
import { getSEOServersideProps, PagePropsWrapper } from 'lib/routing/nextPageUtils'
import {
	FeaturedConstraint,
	NotHiddenConstraint,
	OrderByNewestConstraint,
	PartnerProductConstraint,
	ProductConstraint,
	TopicProductConstraint
} from 'services/ContentfulService/constraints'
import { ContentfulErrors, referencesMultiple, RequestRedirect } from 'services/ContentfulService/utils'
import { Article, ArticleSummary, summarizeArticles } from 'services/ContentfulService/models/articles'
import { fetchArticles } from 'services/ContentfulService/hooks/articles'
import { getTreeIds, LegacyProductLearnRedirects, Product } from 'services/ContentfulService/models/product'
import { LearnProductPage } from 'experiences/learn/products/ProductPage'
import { getTopics } from 'services/ContentfulService/hooks/topics'
import { Topic, TopicTypeSorter, VisibleTopics } from 'services/ContentfulService/models/topics'
import { FAQ } from 'services/ContentfulService/models/fact'
import { Partner } from 'services/ContentfulService/models/partner/partner'
import { NamedPromises } from 'lib'
import { getPartners } from 'services/ContentfulService/models/partner/hooks'
import { getFaqsByProduct } from 'services/ContentfulService/hooks/faqs'
import { StandardLayout } from 'shared/layout/StandardLayout'
import { ObjectNotFoundError } from 'services/ContentfulService/errors'
import { getProducts } from 'lib/cache/productTree'

interface Props {
	selectedProduct: Product
	initialArticles: ArticleSummary[]
	products: Product[]
	relatedTopics: Topic[]
	latestArticles: ArticleSummary[]
	faqs: FAQ[]
	partners: Partner[]
}

const ProductLearnPage: NextPage<PagePropsWrapper<Props>> = ({ metaTags, pageProps }) => (
	<StandardLayout
		metaTags={metaTags}
		schemas={[{ '@type': 'FAQ', faqs: pageProps.faqs, title: `${pageProps.selectedProduct.name} FAQs` }]}
	>
		<LearnProductPage
			partners={pageProps.partners}
			faqs={pageProps.faqs}
			articles={pageProps.initialArticles}
			products={pageProps.products}
			selectedProduct={pageProps.selectedProduct}
			relatedTopics={VisibleTopics(pageProps.relatedTopics).sort(TopicTypeSorter)}
			latestArticles={pageProps.latestArticles}
		/>
	</StandardLayout>
)

// Probably going to do something different here
export const getServerSideProps: GetServerSideProps = getSEOServersideProps<Props>(
	async function (ctx) {
		const productString = ctx.query.product
		if (typeof productString != 'string') throw ContentfulErrors.NotFound
		const products = await getProducts()
		const product = products.find(p => p.slug === productString)
		if (!product) {
			const redirect = products.find(p => p.slug === LegacyProductLearnRedirects[productString])
			if (redirect) throw new RequestRedirect(redirect.relativeLearnPath)
			throw new ObjectNotFoundError('Learn Product')
		}
		const treeProductIds = getTreeIds(product)
		const partnersPromise = referencesMultiple<Partner>(treeProductIds, id =>
			getPartners({ constraints: [PartnerProductConstraint(id)], count: 6 })
		)
		const articlesPromise = referencesMultiple<Article>(treeProductIds, id =>
			fetchArticles({ constraints: [ProductConstraint(id), FeaturedConstraint()], count: 3 })
		)
		const topicsPromise = referencesMultiple<Topic>(treeProductIds, id =>
			getTopics({ constraints: [TopicProductConstraint(id), NotHiddenConstraint()], count: 5 })
		)
		const latestArticlesPromise = referencesMultiple<Article>(treeProductIds, id =>
			fetchArticles({ constraints: [OrderByNewestConstraint(), ProductConstraint(id)], count: 8 })
		)

		return await NamedPromises({
			selectedProduct: Promise.resolve(product),
			faqs: getFaqsByProduct(product),
			partners: partnersPromise,
			products: Promise.resolve(products),
			initialArticles: articlesPromise.then(summarizeArticles),
			relatedTopics: topicsPromise,
			latestArticles: latestArticlesPromise.then(summarizeArticles)
		})
	},
	(metadataTags, pageProps) => {
		const url = metadataTags.url.split('?')[0]
		return {
			...metadataTags,
			url: url,
			description: pageProps.selectedProduct.subtitle,
			imageUrl: pageProps.selectedProduct.graphic?.url,
			title: `Learn About ${pageProps.selectedProduct.name} | Fiona`
		}
	}
)

export default ProductLearnPage
